import { AnimatePresence, motion } from "framer-motion";
import getConfig from "next/config";
import { func, string } from "prop-types";
import React, { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { twMerge } from "tailwind-merge";

import api from "api";

import { CustomerCreationFormAddress } from "./CustomerCreationFormAddress";

import { Button } from "@components/shared/Button";
import Checkbox from "@components/shared/Checkbox";
import Divider from "@components/shared/Divider";
import Icon from "@components/shared/Icons/Icon";
import WarningIcon from "components/shared/Icons/Warning";
import Radio from "@components/shared/Radio";
import SelectField from "@components/shared/SelectField";
import TelephoneField from "@components/shared/TelephoneField";
import TextField from "@components/shared/TextField";
import Typography from "@components/shared/Typography";

import { useFormattedDeal } from "@hooks/useFormattedDeal";
import { useIsB2BSelected } from "@hooks/useIsB2BSelected";

import { useTranslation, Trans } from "@lib/i18n";
import { getCivilityOptions } from "@lib/getCivilityOptions";
import { getIsCountry } from "@lib/getIsCountry";

import { setIsGuestAction } from "redux/actions/authActions";
import { setCurrentDealAction } from "redux/actions/dealActions";
import {
  useAddressRegexPatternsSelector,
  useCompanyInformationRegexPatternsSelector,
  useIsReversedDeliveryFieldsOrderSelector,
  useIsTelephoneFieldEnabledSelector,
  usePersonalInformationRegexPatternsSelector,
} from "@redux/reducers/appConfig";
import { useDealSelector } from "@redux/reducers/deal";

import {
  COUNTRIES,
  GUEST_CHECKOUT_ADDRESS_PREFIXES,
  GUEST_CHECKOUT_CONSENTS,
  USER_FIELDS_NAMES,
} from "@shared/constants";
import { getIsTINRelevantForCountry } from "@shared/v2/lib/getIsTINRelevantForCountry";
import { getIsTVARelevantForCountry } from "@shared/v2/lib/getIsTVARelevantForCountry";

const {
  COMPANY_INFORMATION,
  GUEST_CHECKOUT_ADDRESS,
  PERSONAL_INFORMATION,
} = USER_FIELDS_NAMES;
const { publicRuntimeConfig } = getConfig();
const { COUNTRY } = publicRuntimeConfig;

const initialValues = {
  company: {
    [COMPANY_INFORMATION.COMPANY_NAME]: "",
    [COMPANY_INFORMATION.SIRET_NUMBER]: "",
    [COMPANY_INFORMATION.TVA_NUMBER]: "",
  },
  personal: {
    [PERSONAL_INFORMATION.CIVILITY]: "",
    [PERSONAL_INFORMATION.NAME]: "",
    [PERSONAL_INFORMATION.SURNAME]: "",
    [PERSONAL_INFORMATION.PHONE]: "",
    [PERSONAL_INFORMATION.TIN]: "",
    [PERSONAL_INFORMATION.EMAIL]: "",
  },
  deliveryAddress: {
    [`delivery${GUEST_CHECKOUT_ADDRESS.PRIMARY_ADDRESS}`]: "",
    [`delivery${GUEST_CHECKOUT_ADDRESS.ADDITIONAL_ADDRESS}`]: "",
    [`delivery${GUEST_CHECKOUT_ADDRESS.CITY}`]: "",
    [`delivery${GUEST_CHECKOUT_ADDRESS.PROVINCE}`]: "",
    [`delivery${GUEST_CHECKOUT_ADDRESS.POST_CODE}`]: "",
    [`delivery${GUEST_CHECKOUT_ADDRESS.COUNTRY}`]: "",
  },
  billingAddress: {
    [`billing${GUEST_CHECKOUT_ADDRESS.PRIMARY_ADDRESS}`]: "",
    [`billing${GUEST_CHECKOUT_ADDRESS.ADDITIONAL_ADDRESS}`]: "",
    [`billing${GUEST_CHECKOUT_ADDRESS.CITY}`]: "",
    [`billing${GUEST_CHECKOUT_ADDRESS.PROVINCE}`]: "",
    [`billing${GUEST_CHECKOUT_ADDRESS.POST_CODE}`]: "",
    [`billing${GUEST_CHECKOUT_ADDRESS.COUNTRY}`]: "",
  },
  registrationAddress: {
    [`registration${GUEST_CHECKOUT_ADDRESS.PRIMARY_ADDRESS}`]: "",
    [`registration${GUEST_CHECKOUT_ADDRESS.ADDITIONAL_ADDRESS}`]: "",
    [`registration${GUEST_CHECKOUT_ADDRESS.CITY}`]: "",
    [`registration${GUEST_CHECKOUT_ADDRESS.PROVINCE}`]: "",
    [`registration${GUEST_CHECKOUT_ADDRESS.POSTAL_CODE}`]: "",
    [`registration${GUEST_CHECKOUT_ADDRESS.COUNTRY}`]: "",
  },
};

const SAME_ADDRESS_MAP = {
  BILLING: {
    [`billing${GUEST_CHECKOUT_ADDRESS.PRIMARY_ADDRESS}`]: [
      `delivery${GUEST_CHECKOUT_ADDRESS.PRIMARY_ADDRESS}`,
    ],
    [`billing${GUEST_CHECKOUT_ADDRESS.ADDITIONAL_ADDRESS}`]: [
      `delivery${GUEST_CHECKOUT_ADDRESS.ADDITIONAL_ADDRESS}`,
    ],
    [`billing${GUEST_CHECKOUT_ADDRESS.CITY}`]: [
      `delivery${GUEST_CHECKOUT_ADDRESS.CITY}`,
    ],
    [`billing${GUEST_CHECKOUT_ADDRESS.PROVINCE}`]: [
      `delivery${GUEST_CHECKOUT_ADDRESS.PROVINCE}`,
    ],
    [`billing${GUEST_CHECKOUT_ADDRESS.POST_CODE}`]: [
      `delivery${GUEST_CHECKOUT_ADDRESS.POST_CODE}`,
    ],
    [`billing${GUEST_CHECKOUT_ADDRESS.COUNTRY}`]: [
      `delivery${GUEST_CHECKOUT_ADDRESS.COUNTRY}`,
    ],
  },
  REGISTRATION: {
    [`registration${GUEST_CHECKOUT_ADDRESS.PRIMARY_ADDRESS}`]: [
      `delivery${GUEST_CHECKOUT_ADDRESS.PRIMARY_ADDRESS}`,
    ],
    [`registration${GUEST_CHECKOUT_ADDRESS.ADDITIONAL_ADDRESS}`]: [
      `delivery${GUEST_CHECKOUT_ADDRESS.ADDITIONAL_ADDRESS}`,
    ],
    [`registration${GUEST_CHECKOUT_ADDRESS.CITY}`]: [
      `delivery${GUEST_CHECKOUT_ADDRESS.CITY}`,
    ],
    [`registration${GUEST_CHECKOUT_ADDRESS.PROVINCE}`]: [
      `delivery${GUEST_CHECKOUT_ADDRESS.PROVINCE}`,
    ],
    [`registration${GUEST_CHECKOUT_ADDRESS.POSTAL_CODE}`]: [
      `delivery${GUEST_CHECKOUT_ADDRESS.POST_CODE}`,
    ],
    [`registration${GUEST_CHECKOUT_ADDRESS.COUNTRY}`]: [
      `delivery${GUEST_CHECKOUT_ADDRESS.COUNTRY}`,
    ],
  },
};

const CustomerCreationForm = props => {
  const { changeEmail, email, onSubmitSuccessCallback } = props;

  const [isRegistrationAddressSame, setIsRegistrationAddressSame] = useState(
    true
  );
  const [isBillingAddressSame, setIsBillingAddressSame] = useState(true);
  const [isWorking, setIsWorking] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const dispatch = useDispatch();

  const deal = useDealSelector();
  const formatDeal = useFormattedDeal();
  const formattedDeal = formatDeal(deal);
  const {
    userProfile: { deliveryData },
  } = formattedDeal;

  const isB2BSelected = useIsB2BSelected();

  const isProvinceRelevantForCountry = getIsCountry(COUNTRIES.IT);
  const isReversedOrder = useIsReversedDeliveryFieldsOrderSelector();
  const isTelephoneFieldEnabled = useIsTelephoneFieldEnabledSelector();
  const isTINRelevantForSelectedCountry = getIsTINRelevantForCountry(COUNTRY);
  const isTVARelevantForSelectedCountry = getIsTVARelevantForCountry(COUNTRY);

  const addressRegexPatterns = useAddressRegexPatternsSelector();
  const companyInfoRegexPatterns = useCompanyInformationRegexPatternsSelector();
  const personalInfoRegexPatterns = usePersonalInformationRegexPatternsSelector();

  const { t } = useTranslation();

  const defaultValues = useMemo(
    () => ({
      ...initialValues.company,
      ...initialValues.personal,
      ...initialValues.deliveryAddress,
      ...initialValues.billingAddress,
      ...initialValues.registrationAddress,
      consents: {
        marketing: "",
        profiling: "",
        thirdPartiesMarketing: "",
      },
      email,
    }),
    [email]
  );

  const {
    clearErrors,
    control,
    formState: { errors },
    handleSubmit,
    register,
    setError,
    setValue,
  } = useForm({ defaultValues });

  const civilityOptions = getCivilityOptions(t);

  const onBillingAddressCheckboxChange = () => {
    setIsBillingAddressSame(prev => {
      const next = !prev;

      // let's just empty fields on form section show
      // same boolean is checked upon form submit
      //  - this way the data will always be synced on submit
      //  - no need for mapping delivery address field to these on change
      if (!next) {
        Object.keys(initialValues.billingAddress).forEach(field =>
          setValue(field, "")
        );
      }

      return next;
    });
  };

  const onRegistrationAddressCheckboxChange = () => {
    setIsRegistrationAddressSame(prev => {
      const next = !prev;

      // let's just empty fields on form section show
      // same boolean is checked upon form submit
      //  - this way the data will always be synced on submit
      //  - no need for mapping delivery address field to these on change
      if (!next) {
        Object.keys(initialValues.registrationAddress).forEach(field =>
          setValue(field, "")
        );
      }

      return next;
    });
  };

  const onSubmit = async data => {
    let errorFound = false;

    Object.keys(data.consents).forEach(consent => {
      if (!data.consents[consent] || !data.consents[consent].length) {
        errorFound = true;
        setError(`consents.${consent}`, true);
      }
    });

    if (errorFound) {
      return;
    }

    setSubmitError(false);
    setIsWorking(true);

    const addressesAndCompany = { ...data };

    if (isBillingAddressSame) {
      Object.keys(SAME_ADDRESS_MAP.BILLING).forEach(field => {
        addressesAndCompany[field] = data[SAME_ADDRESS_MAP.BILLING[field]];
      });
    }

    if (isRegistrationAddressSame) {
      Object.keys(SAME_ADDRESS_MAP.REGISTRATION).forEach(field => {
        addressesAndCompany[field] = data[SAME_ADDRESS_MAP.REGISTRATION[field]];
      });
    }

    // - email field has "disabled" attribute to prevent user from changes and show it greyed out
    //    - react-hook-form will make this field undefined because of this
    //    - we need to add it to payload manually here
    // - consents values are strings - let's transform them to booleans
    const payload = {
      ...addressesAndCompany,
      consents: {
        ...Object.keys(data.consents).reduce((acc, cur) => {
          acc[cur] = JSON.parse(data.consents[cur]);
          return acc;
        }, {}),
        disclaimer: true,
        thirdPartiesProfiling: false,
      },
      deliveryData,
      email,
    };

    try {
      const response = await api.postCreateForeignAccount(payload);
      const { lead, returnCode } = response.data;

      if (returnCode !== "OK") {
        throw new Error(
          `Create-Foreign-Account call passed, but returnCode is '${returnCode}'`
        );
      }

      dispatch(setCurrentDealAction(lead));
      dispatch(setIsGuestAction(true));

      onSubmitSuccessCallback({
        ...addressesAndCompany,
        email,
        userFirstName: addressesAndCompany.name,
        userLastName: addressesAndCompany.surname,
      });
    } catch (error) {
      console.error(error);
      setSubmitError(true);
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <div className="flex flex-col items-start gap-4 py-1">
      <form
        className="flex flex-col items-start w-full"
        onSubmit={handleSubmit(onSubmit)}
      >
        {isB2BSelected && (
          <div className="flex flex-col items-start w-full order-0">
            <Typography className="font-semibold">
              <Trans i18nKey={"myDetails.companyDetail"} />
            </Typography>
            <TextField
              errorHelperMessage={t(
                `myDetails.helperTexts.${COMPANY_INFORMATION.COMPANY_NAME}`
              )}
              fullWidth
              label={t(`myDetails.inputs.${COMPANY_INFORMATION.COMPANY_NAME}`)}
              name={COMPANY_INFORMATION.COMPANY_NAME}
              ref={register}
              required
              placeholder={t(
                `myDetails.placeholders.${COMPANY_INFORMATION.COMPANY_NAME}`
              )}
            />
            <TextField
              errorHelperMessage={t(
                `myDetails.helperTexts.${COMPANY_INFORMATION.SIRET_NUMBER}`
              )}
              fullWidth
              label={t(`myDetails.inputs.${COMPANY_INFORMATION.SIRET_NUMBER}`)}
              name={COMPANY_INFORMATION.SIRET_NUMBER}
              pattern={
                companyInfoRegexPatterns[COMPANY_INFORMATION.SIRET_NUMBER]
              }
              ref={register}
              required={!getIsCountry(COUNTRIES.GB)}
              title={t(`myDetails.titles.${COMPANY_INFORMATION.SIRET_NUMBER}`)}
              placeholder={t(
                `myDetails.placeholders.${COMPANY_INFORMATION.SIRET_NUMBER}`
              )}
            />
            {isTVARelevantForSelectedCountry && (
              <TextField
                fullWidth
                errorHelperMessage={t(
                  `myDetails.helperTexts.${COMPANY_INFORMATION.TVA_NUMBER}`
                )}
                label={t(`myDetails.inputs.${COMPANY_INFORMATION.TVA_NUMBER}`)}
                name={COMPANY_INFORMATION.TVA_NUMBER}
                pattern={
                  companyInfoRegexPatterns[COMPANY_INFORMATION.TVA_NUMBER]
                }
                ref={register}
                title={t(`myDetails.titles.${COMPANY_INFORMATION.TVA_NUMBER}`)}
                placeholder={t(
                  `myDetails.placeholders.${COMPANY_INFORMATION.TVA_NUMBER}`
                )}
              />
            )}
            <Divider className="bg-greyLight my-8" />
          </div>
        )}

        {/* Personal Details */}
        <div className="flex flex-col items-start w-full order-1">
          <Typography className="font-semibold">
            <Trans i18nKey={"myDetails.personalDetail"} />
          </Typography>
          <SelectField
            fullWidth
            label={t(`myDetails.inputs.${PERSONAL_INFORMATION.CIVILITY}`)}
            name={PERSONAL_INFORMATION.CIVILITY}
            options={civilityOptions}
            ref={register}
            required
          />
          <TextField
            errorHelperMessage={t(
              `myDetails.helperTexts.${PERSONAL_INFORMATION.USER_FIRST_NAME}`
            )}
            fullWidth
            label={t(
              `myDetails.inputs.${PERSONAL_INFORMATION.USER_FIRST_NAME}`
            )}
            name={PERSONAL_INFORMATION.NAME}
            pattern={
              personalInfoRegexPatterns[PERSONAL_INFORMATION.USER_FIRST_NAME]
            }
            ref={register}
            required
            placeholder={t(
              `myDetails.placeholders.${PERSONAL_INFORMATION.USER_FIRST_NAME}`
            )}
          />
          <TextField
            errorHelperMessage={t(
              `myDetails.helperTexts.${PERSONAL_INFORMATION.USER_LAST_NAME}`
            )}
            fullWidth
            label={t(`myDetails.inputs.${PERSONAL_INFORMATION.USER_LAST_NAME}`)}
            name={PERSONAL_INFORMATION.SURNAME}
            pattern={
              personalInfoRegexPatterns[PERSONAL_INFORMATION.USER_LAST_NAME]
            }
            ref={register}
            required
            placeholder={t(
              `myDetails.placeholders.${PERSONAL_INFORMATION.USER_LAST_NAME}`
            )}
          />
          {isTelephoneFieldEnabled ? (
            <TelephoneField
              errorHelperMessage={t(
                `myDetails.helperTexts.${PERSONAL_INFORMATION.PHONE}`
              )}
              fullWidth
              label={t(`myDetails.inputs.${PERSONAL_INFORMATION.PHONE}`)}
              name={PERSONAL_INFORMATION.PHONE}
              pattern={personalInfoRegexPatterns[PERSONAL_INFORMATION.PHONE]}
              ref={register}
              required
              placeholder={t(
                `myDetails.placeholders.${PERSONAL_INFORMATION.PHONE}`
              )}
            />
          ) : (
            <TextField
              errorHelperMessage={t(
                `myDetails.helperTexts.${PERSONAL_INFORMATION.PHONE}`
              )}
              fullWidth
              label={t(`myDetails.inputs.${PERSONAL_INFORMATION.PHONE}`)}
              name={PERSONAL_INFORMATION.PHONE}
              pattern={personalInfoRegexPatterns[PERSONAL_INFORMATION.PHONE]}
              ref={register}
              required
              placeholder={t(
                `myDetails.placeholders.${PERSONAL_INFORMATION.PHONE}`
              )}
            />
          )}
          {isTINRelevantForSelectedCountry &&
            !(isB2BSelected && getIsCountry(COUNTRIES.ES)) && (
              <TextField
                errorHelperMessage={t(
                  `myDetails.helperTexts.${PERSONAL_INFORMATION.TIN}`
                )}
                fullWidth
                label={t(`myDetails.inputs.${PERSONAL_INFORMATION.TIN}`)}
                name={PERSONAL_INFORMATION.TIN}
                pattern={personalInfoRegexPatterns[PERSONAL_INFORMATION.TIN]}
                ref={register}
                required
                placeholder={t(
                  `myDetails.placeholders.${PERSONAL_INFORMATION.TIN}`
                )}
              />
            )}
          <TextField
            disabled
            errorHelperMessage={t(
              `myDetails.helperTexts.${PERSONAL_INFORMATION.EMAIL}`
            )}
            fullWidth
            label={t(`myDetails.inputs.${PERSONAL_INFORMATION.EMAIL}`)}
            name={PERSONAL_INFORMATION.EMAIL}
            readonly
            ref={register}
            type="email"
            placeholder={t(
              `myDetails.placeholders.${PERSONAL_INFORMATION.EMAIL}`
            )}
          />
          <button
            className="text-black text-xs font-semibold mt-2 ml-auto cursor-pointer transition duration-150 hover:text-primaryOrange hover:scale-105 active:scale-95"
            onClick={changeEmail}
            type="button"
          >
            <Trans i18nKey={"myDetails.changeEmail"} />
          </button>
          <Divider className="bg-greyLight my-4" />
        </div>

        {/* Delivery Address */}
        <div className="flex flex-col items-start w-full order-2">
          <Typography className="font-semibold">
            <Trans i18nKey={"myDetails.deliveryAddressDetail"} />
          </Typography>
          <CustomerCreationFormAddress
            isProvinceRelevantForCountry={isProvinceRelevantForCountry}
            prefix={GUEST_CHECKOUT_ADDRESS_PREFIXES.DELIVERY}
            regexPatterns={addressRegexPatterns}
            register={register}
          />
          <Divider className="bg-greyLight my-4" />
        </div>

        {/* Billing Address */}
        <div
          className={twMerge(
            "flex flex-col items-start w-full",
            isReversedOrder ? "order-4" : "order-3"
          )}
        >
          <Checkbox
            checked={isBillingAddressSame}
            className="my-4"
            label={t("myDetails.inputs.sameBillingAddress")}
            onChange={onBillingAddressCheckboxChange}
          />
          <AnimatePresence initial={false}>
            <motion.div
              animate={isBillingAddressSame ? "collapsed" : "open"}
              exit="collapsed"
              initial={isBillingAddressSame ? "collapsed" : "open"}
              style={{ overflow: "hidden", width: "100%" }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              variants={{
                open: { opacity: 1, height: "auto", pointerEvents: "initial" },
                collapsed: { opacity: 0, height: 0, pointerEvents: "none" },
              }}
            >
              <Typography className="font-semibold mb-4">
                <Trans i18nKey={"myDetails.billingAddressDetail"} />
              </Typography>
              <Typography>
                <Trans i18nKey={"myDetails.billingAddressText"} />
              </Typography>
              <CustomerCreationFormAddress
                isProvinceRelevantForCountry={isProvinceRelevantForCountry}
                prefix={GUEST_CHECKOUT_ADDRESS_PREFIXES.BILLING}
                regexPatterns={addressRegexPatterns}
                register={register}
                required={!isBillingAddressSame}
              />
              {!isReversedOrder && <Divider className="bg-greyLight my-4" />}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Registration Address */}
        <div
          className={twMerge(
            "flex flex-col items-start w-full",
            isReversedOrder ? "order-3" : "order-4"
          )}
        >
          <Checkbox
            checked={isRegistrationAddressSame}
            className="my-4"
            label={t("myDetails.inputs.sameRegistrationAddress")}
            onChange={onRegistrationAddressCheckboxChange}
          />
          <AnimatePresence initial={false}>
            <motion.div
              animate={isRegistrationAddressSame ? "collapsed" : "open"}
              exit="collapsed"
              initial={isRegistrationAddressSame ? "collapsed" : "open"}
              style={{ overflow: "hidden", width: "100%" }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              variants={{
                open: { opacity: 1, height: "auto", pointerEvents: "initial" },
                collapsed: { opacity: 0, height: 0, pointerEvents: "none" },
              }}
            >
              <Typography className="font-semibold mb-4">
                <Trans i18nKey={"myDetails.registrationAddressDetail"} />
              </Typography>
              <Typography>
                <Trans i18nKey={"myDetails.registrationAddressText"} />
              </Typography>
              <CustomerCreationFormAddress
                isProvinceRelevantForCountry={isProvinceRelevantForCountry}
                prefix={GUEST_CHECKOUT_ADDRESS_PREFIXES.REGISTRATION}
                regexPatterns={addressRegexPatterns}
                register={register}
                required={!isRegistrationAddressSame}
              />
              {isReversedOrder && <Divider className="bg-greyLight my-4" />}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Consents */}
        <div className="flex flex-col items-start w-full order-5">
          <Divider className="bg-greyLight my-4" />
          <div className="flex flex-col gap-8 py-8">
            <Typography>
              <Trans
                components={{
                  a: (
                    <a
                      className="text-primaryOrange underline"
                      target="_blank"
                    />
                  ),
                }}
                i18nKey={"myDetails.consents.disclaimer"}
              />
            </Typography>
            {GUEST_CHECKOUT_CONSENTS.map(consent => (
              <div className="flex flex-col gap-4 w-full" key={consent}>
                <Typography className="font-semibold">
                  <Trans i18nKey={`myDetails.consents.${consent}.title`} />
                </Typography>
                <Typography className="text-sm">
                  <Trans
                    components={{
                      a: (
                        <a
                          className="text-primaryOrange underline"
                          target="_blank"
                        />
                      ),
                    }}
                    i18nKey={`myDetails.consents.${consent}.text`}
                  />
                </Typography>
                <Controller
                  control={control}
                  name={`consents.${consent}`}
                  render={({ name, onChange, value }) => (
                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-start">
                      <Radio
                        className=""
                        label={t("myDetails.consents.agree")}
                        name={name}
                        onChange={e => {
                          clearErrors(name);
                          onChange(e);
                        }}
                        checked={value === "true"}
                        value={true}
                      />
                      <Radio
                        className=""
                        label={t("myDetails.consents.decline")}
                        name={name}
                        onChange={e => {
                          clearErrors(name);
                          onChange(e);
                        }}
                        checked={value === "false"}
                        value={false}
                      />
                    </div>
                  )}
                />
                {!!errors?.consents?.[consent] && (
                  <Typography className="text-red text-sm">
                    <Trans i18nKey={"myDetails.consents.error"} />
                  </Typography>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Yeah Buddy!! */}
        <div className="flex flex-col items-center w-full order-6">
          <AnimatePresence initial={false}>
            <motion.div
              animate={submitError ? "open" : "collapsed"}
              exit="collapsed"
              initial={submitError ? "open" : "collapsed"}
              style={{ overflow: "hidden", width: "100%" }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              variants={{
                open: { opacity: 1, height: "auto", pointerEvents: "initial" },
                collapsed: { opacity: 0, height: 0, pointerEvents: "none" },
              }}
            >
              <div className="flex justify-center items-center gap-4 text-sm py-4 w-full">
                <Icon className="size-10 text-red" icon={<WarningIcon />} />
                <Trans>{t("myDetails.submitError")}</Trans>
              </div>
            </motion.div>
          </AnimatePresence>
          <Button
            className="my-4"
            isLoading={isWorking}
            type="submit"
            variant="primary"
          >
            {t("myDetails.submit")}
          </Button>
        </div>
      </form>
    </div>
  );
};

CustomerCreationForm.propTypes = {
  changeEmail: func,
  email: string,
  onSubmitSuccessCallback: func,
};

export default CustomerCreationForm;
